import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { format } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { ArrowLeft, Clock, Mountain, Loader2, Globe, Shield, Minus, Plus, AlertCircle } from "lucide-react";
import { useLang } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOUR_INFO = {
  single: { price: 115, maxPersons: 1 },
  double: { price: 165, maxPersons: 2 },
};

/* ── Safe helpers ── */
function safeArray(val) {
  if (Array.isArray(val)) return val;
  return [];
}

function safeString(val) {
  if (typeof val === "string") return val;
  return "";
}

function safeNumber(val, fallback) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function safeFormatDate(d, fmt, opts) {
  try {
    if (d instanceof Date && !isNaN(d.getTime())) {
      return format(d, fmt, opts || {});
    }
  } catch { /* ignore */ }
  return "";
}

/* ── Component ── */
export default function BookingPage() {
  const { tourType } = useParams();
  const navigate = useNavigate();
  const { t, toggleLang, lang } = useLang();
  const tour = TOUR_INFO[tourType];

  const [date, setDate] = useState(undefined);
  const [timeSlot, setTimeSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [numQuads, setNumQuads] = useState(1);
  const [licenseBConfirmed, setLicenseBConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [tourCfg, setTourCfg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tours/config`)
      .then((res) => {
        if (!cancelled && res?.data && typeof res.data === "object" && res.data[tourType]) {
          setTourCfg(res.data[tourType]);
        }
      })
      .catch(() => { /* fallback ai valori statici */ });
    return () => { cancelled = true; };
  }, [tourType]);

  useEffect(() => {
    if (!tour) navigate("/");
  }, [tour, navigate]);

  /* ── Fetch slots (defensive) ── */
  useEffect(() => {
    if (!date) return;
    let cancelled = false;

    const fetchSlots = async () => {
      setLoading(true);
      setSlotsError(false);
      setTimeSlot("");
      try {
        const dateStr = safeFormatDate(date, "yyyy-MM-dd");
        if (!dateStr) { setLoading(false); return; }

        const res = await axios.get(`${API}/slots?booking_date=${dateStr}&tour_type=${tourType}`);

        if (cancelled) return;

        // Handle ANY response shape
        let parsed = [];
        if (Array.isArray(res?.data)) {
          parsed = res.data;
        } else if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) {
          // Maybe the API wrapped it: { slots: [...] } or { data: [...] }
          const candidate = res.data.slots || res.data.data || res.data.results;
          if (Array.isArray(candidate)) {
            parsed = candidate;
          }
        }

        // Validate each slot object
        const validated = [];
        for (let i = 0; i < parsed.length; i++) {
          const s = parsed[i];
          if (s && typeof s === "object" && s.time_slot) {
            validated.push({
              time_slot: safeString(s.time_slot),
              available_quads: safeNumber(s.available_quads, 0),
              total_quads: safeNumber(s.total_quads, 7),
              is_closed: Boolean(s.is_closed),
            });
          }
        }

        setSlots(validated);
      } catch (err) {
        if (!cancelled) {
          setSlotsError(true);
          setSlots([]);
          toast.error(t("errorSlots"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSlots();
    return () => { cancelled = true; };
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tour) return null;

  /* ── Derived state (all safe) ── */
  const safeSlots = safeArray(slots);
  const price = Number(tourCfg?.price) || tour.price;
  const totalPrice = price * numQuads;

  let selectedSlot = null;
  try {
    if (timeSlot && safeSlots.length > 0) {
      selectedSlot = safeSlots.find((s) => s && s.time_slot === timeSlot) || null;
    }
  } catch { selectedSlot = null; }

  const maxAvailQuads = safeNumber(selectedSlot?.available_quads, 7);

  const tourNameKey = tourType === "single" ? "tourSingleName" : "tourDoubleName";
  const tourPersonsKey = tourType === "single" ? "tourSinglePersons" : "tourDoublePersons";

  const isFormComplete = Boolean(
    date && timeSlot && form.name && form.email && form.phone &&
    licenseBConfirmed && termsAccepted && privacyAccepted
  );

  const calendarLocale = lang === "it" ? itLocale : undefined;

  /* ── Submit (defensive) ── */
  const handleSubmit = async () => {
    try {
      if (!date || !timeSlot) { toast.error(t("selectDateTime")); return; }
      if (!form.name || !form.email || !form.phone) { toast.error(t("fillAllFields")); return; }
      if (!licenseBConfirmed) { toast.error(t("licenseBError")); return; }
      if (!termsAccepted) { toast.error(t("termsError")); return; }
      if (!privacyAccepted) { toast.error(t("privacyError")); return; }

      const dateStr = safeFormatDate(date, "yyyy-MM-dd");
      if (!dateStr) { toast.error(t("selectDateTime")); return; }

      setSubmitting(true);

      const res = await axios.post(`${API}/checkout`, {
        tour_type: tourType,
        booking_date: dateStr,
        time_slot: timeSlot,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        num_quads: numQuads,
        include_video: false,
        license_b_confirmed: licenseBConfirmed,
        language: lang,
        origin_url: window.location.origin,
      });

      const url = res?.data?.url;
      if (typeof url === "string" && url.length > 0) {
        window.location.href = url;
      } else {
        toast.error(t("errorBooking"));
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("errorBooking"));
      setSubmitting(false);
    }
  };

  /* ── Render slot buttons (safe) ── */
  const renderSlots = () => {
    try {
      if (slotsError) {
        return (
          <div className="flex items-center gap-2 text-red-400/80 text-sm">
            <AlertCircle className="w-4 h-4" />
            {lang === "it" ? "Impossibile caricare gli orari. Riprova." : "Unable to load time slots. Please retry."}
          </div>
        );
      }

      if (safeSlots.length === 0) {
        return (
          <p className="text-[#8faa8f] text-sm">
            {lang === "it" ? "Nessun orario disponibile per questa data." : "No time slots available for this date."}
          </p>
        );
      }

      return (
        <div className="grid grid-cols-3 gap-3">
          {safeSlots.map((slot, idx) => {
            const ts = safeString(slot?.time_slot);
            const avail = safeNumber(slot?.available_quads, 0);
            const isSelected = timeSlot === ts;
            const isDisabled = avail <= 0;

            return (
              <button
                key={ts || idx}
                data-testid={`slot-${ts.replace(":", "")}`}
                disabled={isDisabled}
                onClick={() => { setTimeSlot(ts); setNumQuads(1); }}
                className={`p-4 border text-center transition-all rounded-lg ${
                  isSelected
                    ? "bg-[#2d7a4a] border-[#2d7a4a] text-white"
                    : avail > 0
                      ? "glass-card text-[#e8f0e8] hover:border-[#2d7a4a]/60"
                      : "bg-[#0a1a0f]/50 border-[#1a2e20] text-[#1a4a2e] cursor-not-allowed"
                }`}
              >
                <Clock className="w-4 h-4 mx-auto mb-1" />
                <span className="font-bold text-lg block">{ts || "--:--"}</span>
                <span className="block text-xs mt-1">{avail} {t("quadAvailable")}</span>
              </button>
            );
          })}
        </div>
      );
    } catch {
      return (
        <div className="flex items-center gap-2 text-red-400/80 text-sm">
          <AlertCircle className="w-4 h-4" />
          {lang === "it" ? "Errore nella visualizzazione degli orari." : "Error displaying time slots."}
        </div>
      );
    }
  };

  /* ── Render ── */
  return (
    <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8]">
      {/* Header */}
      <header className="border-b border-[#1a4a2e] px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-[#8faa8f] hover:text-[#2d7a4a] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Mountain className="w-4 h-4 text-[#2d7a4a]" />
            <span className="font-bold uppercase tracking-wider text-sm text-[#2d7a4a]">
              {t(tourNameKey)}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-2xl font-bold text-[#d4a056]">€{price}</span>
            <button
              data-testid="lang-toggle-booking"
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1 rounded-full glass-card text-xs text-[#d4a056]"
            >
              <Globe className="w-3 h-3" /> {t("langToggle")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <h1
          className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-2 animate-slide-up"
          data-testid="booking-title"
        >
          {t("bookingTitle")}
        </h1>
        <p className="text-[#8faa8f] mb-10 animate-slide-up-delay-1">
          {t(tourPersonsKey)} — {t("duration")}
        </p>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Calendar */}
          <div className="animate-slide-up-delay-2">
            <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-4 block">
              {t("step1")}
            </label>
            <div className="glass-card rounded-xl p-4 inline-block" data-testid="calendar-container">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={calendarLocale}
                disabled={(d) => {
                  try {
                    const dayAfterTomorrow = new Date();
                    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
                    dayAfterTomorrow.setHours(0, 0, 0, 0);
                    return d < dayAfterTomorrow;
                  } catch { return true; }
                }}
                className="text-[#e8f0e8]"
                classNames={{
                  day_selected: "bg-[#2d7a4a] text-white hover:bg-[#3a9960] hover:text-white focus:bg-[#2d7a4a] focus:text-white",
                  day_today: "bg-[#1a2e20] text-[#2d7a4a] font-bold",
                  day: "h-9 w-9 p-0 font-normal text-[#e8f0e8] hover:bg-[#1a2e20] rounded-md",
                  head_cell: "text-[#8faa8f] font-normal text-[0.8rem] w-9",
                  caption_label: "text-sm font-bold text-[#e8f0e8] uppercase",
                  nav_button: "border border-[#1a4a2e] text-[#8faa8f] hover:bg-[#1a2e20] hover:text-[#2d7a4a] h-7 w-7 bg-transparent p-0 rounded-md",
                  cell: "text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                  day_outside: "text-[#1a4a2e]",
                  day_disabled: "text-[#1a4a2e] opacity-50",
                }}
              />
            </div>

            {date && (
              <p className="mt-4 text-[#8faa8f] text-sm">
                {t("selectedDate")}{" "}
                <span className="text-[#d4a056] font-bold">
                  {safeFormatDate(date, "d MMMM yyyy", { locale: calendarLocale })}
                </span>
              </p>
            )}
          </div>

          {/* Right: Time slots + Form */}
          <div className="space-y-8">
            {/* Time Slots */}
            {date && (
              <div data-testid="time-slots-section" className="animate-fade-in">
                <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-4 block">
                  {t("step2")}
                </label>
                {loading ? (
                  <div className="flex items-center gap-2 text-[#8faa8f]">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t("loading")}
                  </div>
                ) : (
                  renderSlots()
                )}
              </div>
            )}

            {/* Num Quads */}
            {timeSlot && (
              <div data-testid="num-quads-section" className="animate-fade-in">
                <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-3 block">
                  {t("numQuads")}
                </label>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="quads-minus"
                    onClick={() => setNumQuads((n) => Math.max(1, n - 1))}
                    disabled={numQuads <= 1}
                    className="w-10 h-10 rounded-lg glass-card flex items-center justify-center text-[#8faa8f] hover:text-[#2d7a4a] disabled:opacity-30 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-3xl font-bold text-[#d4a056] min-w-[3rem] text-center" data-testid="quads-count">
                    {numQuads}
                  </span>
                  <button
                    data-testid="quads-plus"
                    onClick={() => setNumQuads((n) => Math.min(maxAvailQuads, n + 1))}
                    disabled={numQuads >= maxAvailQuads}
                    className="w-10 h-10 rounded-lg glass-card flex items-center justify-center text-[#8faa8f] hover:text-[#2d7a4a] disabled:opacity-30 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-[#5a7a5a]">/ {maxAvailQuads} {t("quadAvailable")}</span>
                </div>
              </div>
            )}

            {/* Customer Form */}
            {timeSlot && (
              <div data-testid="customer-form" className="animate-fade-in">
                <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-4 block">
                  {t("step3")}
                </label>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("fullName")}</Label>
                    <Input
                      data-testid="input-name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Mario Rossi"
                      className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#2d7a4a] focus:ring-[#2d7a4a] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("email")}</Label>
                    <Input
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="mario@email.com"
                      className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#2d7a4a] focus:ring-[#2d7a4a] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#8faa8f] text-xs uppercase tracking-wider">{t("phone")}</Label>
                    <Input
                      data-testid="input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="+39 333 1234567"
                      className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#2d7a4a] focus:ring-[#2d7a4a] rounded-lg h-12 mt-1 placeholder:text-[#3a5a3a]"
                    />
                  </div>

                  {/* License B Checkbox */}
                  <div className="pt-2">
                    <button
                      data-testid="license-b-checkbox"
                      onClick={() => setLicenseBConfirmed(!licenseBConfirmed)}
                      className={`w-full flex items-center gap-3 p-4 border rounded-lg transition-all ${
                        licenseBConfirmed
                          ? "bg-[#2d7a4a]/10 border-[#2d7a4a]"
                          : "glass-card hover:border-[#d4a056]/40"
                      }`}
                    >
                      <div className={`w-5 h-5 border-2 flex items-center justify-center rounded flex-shrink-0 ${
                        licenseBConfirmed ? "bg-[#2d7a4a] border-[#2d7a4a]" : "border-[#5a7a5a]"
                      }`}>
                        {licenseBConfirmed && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                      <div className="text-left">
                        <span className="text-sm text-[#e8f0e8] font-semibold block">{t("licenseBLabel")}</span>
                        <span className="text-xs text-[#8faa8f] flex items-center gap-1 mt-0.5">
                          <Shield className="w-3 h-3 text-[#d4a056]" /> {t("licenseBRequired")}
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Terms & Conditions Checkbox */}
                  <div>
                    <button
                      data-testid="terms-checkbox"
                      onClick={() => setTermsAccepted(!termsAccepted)}
                      className={`w-full flex items-center gap-3 p-4 border rounded-lg transition-all ${
                        termsAccepted
                          ? "bg-[#2d7a4a]/10 border-[#2d7a4a]"
                          : "glass-card hover:border-[#d4a056]/40"
                      }`}
                    >
                      <div className={`w-5 h-5 border-2 flex items-center justify-center rounded flex-shrink-0 ${
                        termsAccepted ? "bg-[#2d7a4a] border-[#2d7a4a]" : "border-[#5a7a5a]"
                      }`}>
                        {termsAccepted && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                      <div className="text-left">
                        <span className="text-sm text-[#e8f0e8] font-semibold block">{t("termsLabel")}</span>
                        <span className="text-xs text-[#8faa8f] flex items-center gap-1 mt-0.5">
                          <Shield className="w-3 h-3 text-[#d4a056]" /> {t("termsRequired")}
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Privacy Policy Checkbox */}
                  <div>
                    <button
                      data-testid="privacy-checkbox"
                      onClick={() => setPrivacyAccepted(!privacyAccepted)}
                      className={`w-full flex items-center gap-3 p-4 border rounded-lg transition-all ${
                        privacyAccepted
                          ? "bg-[#2d7a4a]/10 border-[#2d7a4a]"
                          : "glass-card hover:border-[#d4a056]/40"
                      }`}
                    >
                      <div className={`w-5 h-5 border-2 flex items-center justify-center rounded flex-shrink-0 ${
                        privacyAccepted ? "bg-[#2d7a4a] border-[#2d7a4a]" : "border-[#5a7a5a]"
                      }`}>
                        {privacyAccepted && <span className="text-white text-xs font-bold">✓</span>}
                      </div>
                      <div className="text-left">
                        <span className="text-sm text-[#e8f0e8] font-semibold block">{t("privacyLabel")}</span>
                        <span className="text-xs text-[#8faa8f] flex items-center gap-1 mt-0.5">
                          <Shield className="w-3 h-3 text-[#d4a056]" /> {t("privacyRequired")}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Summary & Submit */}
            {isFormComplete && (
              <div data-testid="booking-summary" className="glass-card rounded-xl p-6 animate-fade-in">
                <h3 className="text-lg font-bold uppercase text-[#e8f0e8] mb-4">{t("summary")}</h3>
                <div className="space-y-2 text-sm text-[#8faa8f]">
                  <div className="flex justify-between">
                    <span>{t("tour")}</span>
                    <span className="text-[#e8f0e8]">{t(tourNameKey)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("date")}</span>
                    <span className="text-[#e8f0e8]">
                      {safeFormatDate(date, "d MMMM yyyy", { locale: calendarLocale })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("time")}</span>
                    <span className="text-[#e8f0e8]">{timeSlot}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("quads")}</span>
                    <span className="text-[#e8f0e8]">{numQuads} x €{price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("name")}</span>
                    <span className="text-[#e8f0e8]">{form.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t("email")}</span>
                    <span className="text-[#e8f0e8] text-xs break-all">{form.email}</span>
                  </div>
                  <div className="border-t border-[#1a4a2e] pt-2 mt-2 flex justify-between text-lg font-bold">
                    <span className="text-[#e8f0e8]">{t("total")}</span>
                    <span className="text-[#d4a056] text-glow-gold">€{totalPrice}</span>
                  </div>
                </div>
                <button
                  data-testid="confirm-booking-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full mt-6 bg-[#2d7a4a] text-white font-bold uppercase tracking-wider py-4 hover:bg-[#3a9960] transition-all active:scale-[0.98] text-sm disabled:opacity-50 rounded-lg"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                  ) : (
                    t("payConfirm")
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
